import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, CalendarSync, RefreshCw, Unplug } from "lucide-react";
import { api, apiError } from "@/lib/api";

const inputCls = "border border-[#E5E5E2] px-3 py-2 text-sm outline-none focus:border-[#1A1A1A] w-full";
const STATUS_COLORS = { confirmed: "bg-green-50 text-green-700", completed: "bg-gray-100 text-gray-600", canceled: "bg-red-50 text-red-600" };
const EMPTY = { client_name: "", email: "", phone: "", category: "", package: "", date: "", start_time: "", duration: "", location: "", price_total: "", notes: "", status: "confirmed" };
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      const hr12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? "AM" : "PM";
      opts.push(`${hr12}:${m === 0 ? "00" : "30"} ${ampm}`);
    }
  }
  return opts;
})();
const DURATION_OPTIONS = ["30 min", "1 hr", "1.5 hr", "2 hr", "2.5 hr", "3 hr", "4 hr", "5 hr", "6 hr", "Full day"];

function dstr(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function money(s) { const m = String(s || "").replace(/[^0-9.]/g, ""); return parseFloat(m) || 0; }

export default function CalendarTab() {
  const [sessions, setSessions] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [open, setOpen] = useState(null); // session object or EMPTY-ish
  const [isNew, setIsNew] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [gcal, setGcal] = useState({ connected: false, email: null, configured: false });
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/admin/sessions").then((r) => setSessions(r.data)).catch((e) => toast.error(apiError(e)));
  const loadGcal = useCallback(() => api.get("/admin/google/status").then((r) => setGcal(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadGcal(); }, [loadGcal]);

  // handle return from Google OAuth
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get("gcal");
    if (v === "connected") toast.success("Google Calendar connected");
    else if (v === "error") toast.error("Google connection failed — please try again");
    if (v) {
      window.history.replaceState({}, "", window.location.pathname + "?tab=calendar");
      loadGcal();
    }
  }, [loadGcal]);

  const connectGoogle = async () => {
    try {
      const r = await api.get("/admin/google/auth-url");
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/google/sync");
      const errs = r.data.errors || [];
      if (errs.length > 0) {
        toast.error(`Synced ${r.data.synced} of ${r.data.total}. ${errs.length} failed: ${errs[0]}`, { duration: 8000 });
      } else {
        toast.success(`Synced ${r.data.synced} of ${r.data.total} sessions to Google Calendar`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e), { duration: 8000 });
    } finally {
      setSyncing(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar? Existing events in Google will stay, but sync stops.")) return;
    await api.post("/admin/google/disconnect");
    setGcal({ connected: false, email: null, configured: gcal.configured });
    toast.success("Disconnected");
  };

  const byDate = useMemo(() => {
    const m = {};
    sessions.forEach((s) => { if (s.date) (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [sessions]);

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setDate(d.getDate() + dir);
    setCursor(d);
  };

  const weekStart = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; };

  const monthCells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const weekDays = useMemo(() => {
    const start = weekStart(cursor);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = Object.fromEntries(fd.entries());
    if (isNew) payload.send_confirmation_email = fd.get("send_confirmation_email") === "true";
    try {
      if (isNew) await api.post("/admin/sessions", payload);
      else await api.put(`/admin/sessions/${open.id}`, payload);
      toast.success("Saved");
      setOpen(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const recordPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) return;
    try {
      const r = await api.post(`/admin/sessions/${open.id}/payments`, { amount: amt });
      setOpen(r.data);
      setPayAmount("");
      load();
      toast.success("Payment recorded");
    } catch (err) { toast.error(apiError(err)); }
  };

  const setStatus = async (status) => {
    try {
      const r = await api.put(`/admin/sessions/${open.id}`, { status });
      setOpen(r.data);
      load();
      toast.success(status === "completed" ? "Session marked completed" : "Session canceled");
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this session permanently?")) return;
    await api.delete(`/admin/sessions/${open.id}`);
    setOpen(null);
    load();
  };

  const title = view === "month"
    ? cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : view === "week"
      ? `${weekStart(cursor).toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${new Date(weekStart(cursor).getTime() + 6 * 864e5).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const SessionChip = ({ s }) => (
    <button onClick={() => { setOpen(s); setIsNew(false); }} data-testid={`session-chip-${s.id}`}
      className={`block w-full text-left text-[11px] px-2 py-1 rounded mb-1 truncate ${STATUS_COLORS[s.status] || STATUS_COLORS.confirmed} hover:opacity-80`}>
      {s.start_time && <span className="font-medium mr-1">{s.start_time}</span>}{s.client_name}
    </button>
  );

  return (
    <div data-testid="admin-calendar">
      {/* Google Calendar sync bar */}
      <div className="bg-white border border-[#E5E5E2] px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="gcal-bar">
        <div className="flex items-center gap-3">
          <CalendarSync size={16} className={gcal.connected ? "text-green-600" : "text-[#B5B5B0]"} />
          {gcal.connected ? (
            <p className="text-sm text-[#4A4A4A]">Connected to Google Calendar <span className="text-[#7A7A7A]">({gcal.email})</span></p>
          ) : (
            <p className="text-sm text-[#7A7A7A]">Connect your Google Calendar to sync sessions and see busy times</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gcal.connected ? (
            <>
              <button onClick={syncNow} disabled={syncing} data-testid="gcal-sync"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-[#E5E5E2] px-4 py-2 hover:border-[#1A1A1A] disabled:opacity-50">
                <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}
              </button>
              <button onClick={disconnectGoogle} data-testid="gcal-disconnect"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] border border-red-200 text-red-600 px-4 py-2 hover:bg-red-50">
                <Unplug size={12} /> Disconnect
              </button>
            </>
          ) : (
            <button onClick={connectGoogle} data-testid="gcal-connect"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 py-2 hover:bg-[#4A4A4A]">
              <CalendarSync size={12} /> Connect Google Calendar
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="p-2 border border-[#E5E5E2] bg-white" data-testid="cal-prev"><ChevronLeft size={15} /></button>
          <button onClick={() => setCursor(new Date())} className="text-xs uppercase tracking-[0.12em] border border-[#E5E5E2] bg-white px-4 py-2" data-testid="cal-today">Today</button>
          <button onClick={() => move(1)} className="p-2 border border-[#E5E5E2] bg-white" data-testid="cal-next"><ChevronRight size={15} /></button>
          <h2 className="font-serif text-2xl ml-3">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {["month", "week", "day"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`text-xs uppercase tracking-[0.12em] px-4 py-2 border ${view === v ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5E2] bg-white"}`}>
              {v}
            </button>
          ))}
          <button onClick={() => { setOpen({ ...EMPTY, date: dstr(cursor) }); setIsNew(true); }} data-testid="cal-add-session"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 py-2.5 hover:bg-[#4A4A4A]">
            <Plus size={13} /> Add Session
          </button>
        </div>
      </div>

      {view === "month" && (
        <div className="bg-white border border-[#E5E5E2]">
          <div className="grid grid-cols-7 border-b border-[#E5E5E2]">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <p key={d} className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A] px-2 py-2">{d}</p>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d, i) => {
              const key = dstr(d);
              const daySessions = byDate[key] || [];
              const inMonth = d.getMonth() === cursor.getMonth();
              const isToday = key === dstr(new Date());
              return (
                <div key={i} data-testid={`cal-cell-${key}`}
                  className={`min-h-[90px] border-b border-r border-[#EFEFEC] p-1.5 ${inMonth ? "" : "bg-[#FAFAF8] text-[#B5B5B0]"} ${isToday ? "bg-amber-50/60" : ""}`}>
                  <p className={`text-xs mb-1 ${isToday ? "font-bold" : ""}`}>{d.getDate()}</p>
                  {daySessions.slice(0, 2).map((s) => <SessionChip key={s.id} s={s} />)}
                  {daySessions.length > 2 && <p className="text-[10px] text-[#7A7A7A] px-1">+{daySessions.length - 2} more</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {weekDays.map((d) => {
            const key = dstr(d);
            return (
              <div key={key} className="bg-white border border-[#E5E5E2] min-h-[160px] p-2" data-testid={`cal-week-${key}`}>
                <p className="text-[10px] uppercase tracking-[0.12em] text-[#7A7A7A] mb-2">
                  {d.toLocaleDateString("en-US", { weekday: "short" })} <span className="text-sm text-[#1A1A1A]">{d.getDate()}</span>
                </p>
                {(byDate[key] || []).map((s) => <SessionChip key={s.id} s={s} />)}
              </div>
            );
          })}
        </div>
      )}

      {view === "day" && (
        <div className="bg-white border border-[#E5E5E2] p-4 space-y-2" data-testid="cal-day">
          {(byDate[dstr(cursor)] || []).length === 0 && <p className="text-sm text-[#7A7A7A] py-6 text-center">No sessions this day.</p>}
          {(byDate[dstr(cursor)] || []).map((s) => (
            <button key={s.id} onClick={() => { setOpen(s); setIsNew(false); }} data-testid={`session-row-${s.id}`}
              className="w-full text-left border border-[#EFEFEC] p-4 hover:border-[#1A1A1A] transition-colors">
              <p className="font-medium">{s.start_time ? `${s.start_time} — ` : ""}{s.client_name} <span className="text-xs text-[#7A7A7A]">({s.category}{s.package ? ` · ${s.package}` : ""})</span></p>
              <p className="text-xs text-[#7A7A7A] mt-1">{s.location || "No location"} · {s.payment_status}</p>
            </button>
          ))}
        </div>
      )}

      {/* Detail / edit panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="session-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(null)} />
          <div className="relative bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">{isNew ? "New Session" : open.client_name}</h3>
              <button onClick={() => setOpen(null)} data-testid="session-close"><X size={18} /></button>
            </div>
            <form onSubmit={save} className="space-y-3" data-testid="session-form">
              <input name="client_name" defaultValue={open.client_name} required placeholder="Client name *" className={inputCls} data-testid="session-client-name" />
              <div className="grid grid-cols-2 gap-3">
                <input name="email" defaultValue={open.email} placeholder="Email" className={inputCls} />
                <input name="phone" defaultValue={open.phone} placeholder="Phone" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input name="category" defaultValue={open.category} placeholder="Photography type" className={inputCls} />
                <input name="location" defaultValue={open.location} placeholder="Location / address" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input name="date" type="date" defaultValue={open.date} required className={inputCls} data-testid="session-date" />
                <select name="start_time" defaultValue={open.start_time} className={inputCls} data-testid="session-time">
                  <option value="">Time</option>
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select name="duration" defaultValue={open.duration} className={inputCls} data-testid="session-duration">
                  <option value="">Duration</option>
                  {DURATION_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input name="price_total" defaultValue={open.price_total} placeholder="Total price" className={inputCls} />
                <select name="status" value={open.status || "confirmed"} onChange={(e) => setOpen({ ...open, status: e.target.value })} className={inputCls} data-testid="session-status">
                  {["confirmed", "completed", "canceled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <textarea name="notes" defaultValue={open.notes} rows={3} placeholder="Notes — clothing, makeup, client requests…" className={`${inputCls} resize-y`} />
            {isNew && (
              <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 hover:border-[#1A1A1A] transition-colors" data-testid="session-send-email-wrap">
                <input type="checkbox" name="send_confirmation_email" value="true" defaultChecked
                  className="accent-[#1A1A1A] w-4 h-4" data-testid="session-send-email" />
                Email session confirmation to client
              </label>
            )}
              <button type="submit" data-testid="session-save"
                className="w-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] py-3 hover:bg-[#4A4A4A]">
                {isNew ? "Add Session" : "Save Changes"}
              </button>
            </form>

            {!isNew && (
              <>
                {open.booking_id && (
                  <a href={`/admin/client/${open.booking_id}`} data-testid="session-open-client"
                    className="block text-center mt-6 text-[11px] uppercase tracking-[0.15em] border border-[#1A1A1A] text-[#1A1A1A] px-4 py-3 hover:bg-[#1A1A1A] hover:text-white transition-colors">
                    Open Client Record — Forms & Contract
                  </a>
                )}
                <div className="mt-6 border-t border-[#E5E5E2] pt-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-3">Payments</p>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Total: {open.price_total || "—"}</span>
                    <span>Paid: ${(open.amount_paid || 0).toFixed(0)}</span>
                    <span className="font-medium">Balance: ${Math.max(money(open.price_total) - (open.amount_paid || 0), 0).toFixed(0)}</span>
                  </div>
                  <p className="text-xs mb-3"><span className={`px-2 py-0.5 ${open.payment_status === "Paid in Full" ? "bg-green-50 text-green-700" : open.payment_status === "Deposit Paid" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{open.payment_status}</span>
                    <span className="text-[#7A7A7A] ml-2">{open.source === "website" ? "via website" : "manual entry"}</span></p>
                  <div className="flex gap-2">
                    <input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} type="number" min="0" placeholder="Amount"
                      className={inputCls} data-testid="session-payment-amount" />
                    <button onClick={recordPayment} data-testid="session-record-payment"
                      className="text-xs uppercase tracking-[0.12em] bg-[#1A1A1A] text-white px-5 whitespace-nowrap hover:bg-[#4A4A4A]">
                      Record
                    </button>
                  </div>
                  {(open.payments || []).map((p, i) => (
                    <p key={i} className="text-xs text-[#7A7A7A] mt-2">+${p.amount} · {new Date(p.at).toLocaleDateString()}{p.note ? ` · ${p.note}` : ""}</p>
                  ))}
                </div>
                <div className="flex gap-2 mt-6">
                  <button onClick={() => setStatus("completed")} className="flex-1 text-xs uppercase tracking-[0.12em] border border-[#E5E5E2] py-2.5 hover:border-[#1A1A1A]">Mark Completed</button>
                  <button onClick={() => setStatus("canceled")} data-testid="session-cancel" className="flex-1 text-xs uppercase tracking-[0.12em] border border-red-200 text-red-600 py-2.5 hover:bg-red-50">Cancel Session</button>
                </div>
                <button onClick={remove} data-testid="session-delete" className="w-full mt-2 text-xs uppercase tracking-[0.12em] text-red-500 py-2">Delete permanently</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
